import React from 'react';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import Paper from '@mui/material/Paper';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { memberProps, useAuth, useMembersContext } from '../../context';
import { useAlert, useFetch } from '../../hooks';
import { Title } from '../../styles';
import {
    Form,
    Field,
    FormType,
    InputEventProps,
    ActionButton,
    Alert
} from '..';

interface AddMemberProps {
    memberRole: 'admin' | 'employee';
    open: boolean;
    closeHandler: () => void;
}

export const AddMembers: React.FC<AddMemberProps> = React.memo(
    ({ memberRole, open, closeHandler }) => {
        const { session } = useAuth();
        const { setAdminMembers, setEmployeeMembers } = useMembersContext();
        const addMemberInitialState: memberProps = {
            Guid: '',
            Name: '',
            Surname: '',
            Email: '',
            Phone: '',
            Contact: '',
            Address: '',
            Active: 1,
            Role: memberRole,
            Company: 0
        };
        const [fetchHandler, loading] = useFetch('/dbAddMember', 'POST');
        const { alert, dispatchAlert, closeAlert } = useAlert();
        const [member, setMember] = React.useState<memberProps>(
            addMemberInitialState
        );

        const onChangeHandler = React.useCallback(
            (key: keyof memberProps) => (e: InputEventProps) =>
                setMember(prevMember => {
                    return {
                        ...prevMember,
                        [key]: e.target.value
                    };
                }),
            []
        );

        const addMemberHandler = React.useCallback(
            async (e: FormType) => {
                e.preventDefault();
                closeHandler();
                const result = await fetchHandler({
                    data: {
                        member_guid: session.MemberGuid,
                        session_guid: session.SessionGuid,
                        data: { ...member, Role: memberRole }
                    }
                });
                if (result?.success) {
                    dispatchAlert({
                        type: 'UPDATE_ALERT',
                        message: 'Added Member!',
                        severity: 'success'
                    });
                    memberRole === 'employee'
                        ? setEmployeeMembers(prevMembers => [
                              ...prevMembers,
                              member
                          ])
                        : setAdminMembers(prevMembers => [
                              ...prevMembers,
                              member
                          ]);
                } else {
                    dispatchAlert({
                        type: 'UPDATE_ALERT',
                        message: result.msg,
                        severity: 'error'
                    });
                }
                dispatchAlert({ type: 'OPEN' });
            },
            [
                closeHandler,
                fetchHandler,
                session.MemberGuid,
                session.SessionGuid,
                member,
                dispatchAlert,
                memberRole,
                setEmployeeMembers,
                setAdminMembers
            ]
        );

        return (
            <>
                <Alert closeHandler={closeAlert} {...alert} />
                <Drawer anchor='left' open={open} onClose={closeHandler}>
                    <Paper
                        sx={{
                            width: '100vw',
                            maxWidth: '70rem',
                            padding: '2rem 5vw',
                            minHeight: '100vh',
                            display: 'flex',
                            flexDirection: 'column'
                            // placeContent: 'center'
                        }}
                    >
                        <Title size='medium'>
                            Add {memberRole === 'admin' ? 'Admin' : 'Employee'}
                        </Title>
                        <Form
                            onSubmit={addMemberHandler}
                            sx={{ marginTop: '1.5rem' }}
                        >
                            <Field
                                name='Name'
                                sm={6}
                                onChange={onChangeHandler('Name')}
                            />
                            <Field
                                name='Surname'
                                sm={6}
                                onChange={onChangeHandler('Surname')}
                            />
                            <Field
                                name='Email'
                                sm={6}
                                onChange={onChangeHandler('Email')}
                            />
                            <Field
                                name='Phone'
                                sm={6}
                                onChange={onChangeHandler('Phone')}
                                optional
                            />
                            <Field
                                name='Address'
                                sm={6}
                                onChange={onChangeHandler('Address')}
                                sx={{ marginRight: '0.5rem' }}
                                multiline
                                optional
                            />
                            <Field
                                name='Contact'
                                label='Emergency Number'
                                sm={3}
                                onChange={onChangeHandler('Contact')}
                                optional
                            />
                            <Field
                                name='Company'
                                label='Works at'
                                sm={3}
                                onChange={onChangeHandler('Company')}
                                select
                            >
                                <MenuItem value={0}>Prisma</MenuItem>
                                <MenuItem value={1}>Prispek</MenuItem>
                                <MenuItem value={2}>Spektra</MenuItem>
                            </Field>
                            <ActionButton
                                type='submit'
                                Icon={<CheckIcon />}
                                color='success'
                                loading={loading}
                                xs={1}
                                grid
                            />
                            <ActionButton
                                type='button'
                                Icon={<CloseIcon />}
                                color='error'
                                onClick={closeHandler}
                                xs={1}
                                grid
                            />
                        </Form>
                    </Paper>
                </Drawer>
            </>
        );
    }
);
